import type { MeetingDetail } from '@fos/shared/contracts/meetings';
import { CalendarDays, Compass, FolderKanban, MapPin, MoreHorizontal, Users } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router';
import { appErrorCode } from '../../../lib/api';
import { useRememberRecent } from '../../../lib/recent';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ContextBanner } from '../../../ui/Blocks';
import { Button, IconButton } from '../../../ui/Button';
import { Markdown } from '../../../ui/Markdown';
import { Menu, MenuContent, MenuTrigger } from '../../../ui/Menu';
import { Page } from '../../../ui/Page';
import { Breadcrumbs, type Crumb } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { TOUCH_BUTTON, TOUCH_TEXT } from '../../mobile';
import { NewTaskDialog } from '../../tasks';
import { CalendarPrivacyBanner } from '../components/CalendarPrivacyBanner';
import { MeetingMenuItems, meetingCanAddTask, useMeetingRecordActions } from '../components/MeetingActions';
import { MeetingAiSection, useProcessMeeting } from '../components/MeetingAiSection';
import { EditMeetingDialog } from '../components/NewMeetingDialog';
import { TranscriptDialog, TranscriptPanel } from '../components/TranscriptPanel';
import { meetingWhenText, participantsText } from '../meeting-format';
import { MeetingsNoAccess } from './MeetingsNoAccess';

/**
 * Widok spotkania `/spotkania/:id` (Figma 05 „Spotkanie z propozycjami zadań”, 3032:3396): okruszki
 * Klienci › klient › Spotkania, tytuł i „⋯”, linia meta (kiedy, uczestnicy, miejsce), notatki, wynik AI
 * (podsumowanie, ustalenia, pytania, propozycje zadań) i transkrypcja w panelu po prawej (na węższych ekranach
 * pod treścią, zwijana).
 */
export function MeetingDetailPage() {
  const access = useAccess();
  if (!access.can('clients')) return <MeetingsNoAccess />;
  return <MeetingScreen />;
}

function MeetingScreen() {
  const { id = '' } = useParams();
  const meeting = trpc.meetings.get.useQuery(
    { id },
    {
      enabled: id !== '',
      // AI pracuje w tle (worker): odświeżanie, aż skończy
      refetchInterval: (query) => (query.state.data?.ai.status === 'queued' ? 3000 : false),
    },
  );

  if (meeting.isPending) {
    return (
      <Page width="reading">
        <div role="status" aria-label="Wczytywanie spotkania" className="flex flex-col gap-4">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="mt-6 h-24 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </Page>
    );
  }
  if (meeting.isError) {
    const notFound = appErrorCode(meeting.error) === 'NOT_FOUND' || appErrorCode(meeting.error) === 'VALIDATION_ERROR';
    return (
      <Page width="reading">
        <Breadcrumbs items={[{ label: 'Spotkania', to: '/spotkania' }]} />
        <Section className="mt-6">
          {notFound ? (
            <EmptyState
              icon={Compass}
              title="Nie ma takiego spotkania"
              description="Mogło zostać usunięte albo nie masz do niego dostępu."
              action={
                <Button asChild variant="secondary" className={TOUCH_BUTTON}>
                  <Link to="/spotkania">Wróć do spotkań</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState onRetry={() => void meeting.refetch()} retrying={meeting.isFetching} />
          )}
        </Section>
      </Page>
    );
  }
  return <MeetingView meeting={meeting.data} />;
}

function MeetingView({ meeting }: { meeting: MeetingDetail }) {
  const access = useAccess();
  useRememberRecent({
    type: 'meeting',
    id: meeting.id,
    title: meeting.title,
    subtitle: ['spotkanie', meeting.client?.name].filter(Boolean).join(' · '),
    to: `/spotkania/${meeting.id}`,
  });
  const [editing, setEditing] = useState(false);
  const [editingTranscript, setEditingTranscript] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  const { run: process, pending: processing } = useProcessMeeting(meeting);
  const actions = useMeetingRecordActions();

  const crumbs: Crumb[] = meeting.client
    ? [
        { label: 'Klienci', to: '/klienci' },
        { label: meeting.client.name, to: `/klienci/${meeting.client.id}` },
        { label: 'Spotkania', to: `/klienci/${meeting.client.id}?zakladka=spotkania` },
      ]
    : [{ label: 'Spotkania', to: '/spotkania' }];

  const canAddTask = meetingCanAddTask(meeting, access.can('tasks', 'edit'));
  const people = participantsText(meeting.participants, 4);

  const noContent = !meeting.notesMd && !meeting.hasTranscript && meeting.ai.status === 'none';

  return (
    <div className="xl:flex xl:min-h-dvh">
      <div className="min-w-0 flex-1">
        <Page width="reading" className="pb-4 md:pt-14 md:pb-6 xl:pb-16">
          <Breadcrumbs items={crumbs} />
          <header className="mt-3 flex items-start gap-2">
            <h1 className="min-w-0 flex-1 text-h3 break-words text-fg">{meeting.title}</h1>
            <Menu>
              <MenuTrigger asChild>
                <IconButton
                  icon={MoreHorizontal}
                  label="Więcej akcji"
                  size={32}
                  iconSize={18}
                  className="max-md:size-11"
                />
              </MenuTrigger>
              <MenuContent align="end" className="w-60">
                <MeetingMenuItems
                  meeting={meeting}
                  actions={actions}
                  onEdit={() => setEditing(true)}
                  onEditTranscript={() => setEditingTranscript(true)}
                  onAddTask={() => setAddingTask(true)}
                  onProcess={process}
                  processing={processing}
                />
              </MenuContent>
            </Menu>
          </header>

          <ul
            aria-label="Szczegóły spotkania"
            className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-body-s text-fg-secondary"
          >
            <MetaItem icon={CalendarDays}>{meetingWhenText(meeting)}</MetaItem>
            {people && <MetaItem icon={Users}>{people}</MetaItem>}
            {meeting.location && <MetaItem icon={MapPin}>{meeting.location}</MetaItem>}
            {meeting.project && (
              <MetaItem icon={FolderKanban}>
                {access.can('projects') ? (
                  <Link
                    to={`/projekty/${meeting.project.id}`}
                    className={`hover:text-fg hover:underline ${TOUCH_TEXT}`}
                  >
                    {meeting.project.name}
                  </Link>
                ) : (
                  meeting.project.name
                )}
              </MetaItem>
            )}
          </ul>

          <CalendarPrivacyBanner meeting={meeting} className="mt-6" />

          {meeting.archivedAt && (
            <ContextBanner tone="info" className="mt-6">
              Spotkanie jest w archiwum. Przywróć je, żeby wprowadzać zmiany.
            </ContextBanner>
          )}

          <div className="mt-6 flex flex-col gap-4 md:gap-6">
            {meeting.notesMd && (
              <Section aria-labelledby="notatki">
                <div className="flex items-center justify-between gap-3">
                  <h2 id="notatki" className="text-body-m font-semibold text-fg">
                    Notatki
                  </h2>
                  {meeting.canEdit && (
                    <Button variant="ghost" size="s" className={TOUCH_BUTTON} onClick={() => setEditing(true)}>
                      Edytuj
                    </Button>
                  )}
                </div>
                <Markdown className="mt-2 break-words">{meeting.notesMd}</Markdown>
              </Section>
            )}

            {noContent ? (
              <Section>
                <EmptyState
                  icon={CalendarDays}
                  title="Brak notatek i transkrypcji"
                  description="Dodaj notatki albo transkrypcję, a AI przygotuje podsumowanie, ustalenia i propozycje zadań."
                  action={
                    meeting.canEdit ? (
                      <Button variant="secondary" onClick={() => setEditingTranscript(true)} className={TOUCH_BUTTON}>
                        Dodaj transkrypcję
                      </Button>
                    ) : undefined
                  }
                />
              </Section>
            ) : (
              <MeetingAiSection meeting={meeting} />
            )}
          </div>
        </Page>
      </div>

      {/* od 1280 px: biała kolumna transkrypcji (jak lista Inboxu); węziej: biały blok pod treścią */}
      <aside
        aria-label="Transkrypcja spotkania"
        className="mx-auto w-full max-w-[784px] px-4 pb-16 md:px-8 xl:mx-0 xl:w-[380px] xl:max-w-none xl:shrink-0 xl:border-l xl:border-line xl:bg-panel xl:px-6 xl:pt-14 xl:pb-12"
      >
        <div className="fos-section rounded-xl border border-line bg-panel p-4 md:px-8 md:py-6 xl:sticky xl:top-14 xl:rounded-none xl:border-0 xl:p-0">
          <TranscriptPanel meeting={meeting} onEdit={() => setEditingTranscript(true)} />
        </div>
      </aside>

      {meeting.canEdit && <EditMeetingDialog open={editing} onOpenChange={setEditing} meeting={meeting} />}
      {meeting.canEdit && (
        <TranscriptDialog open={editingTranscript} onOpenChange={setEditingTranscript} meeting={meeting} />
      )}
      {canAddTask && (
        <NewTaskDialog
          open={addingTask}
          onOpenChange={setAddingTask}
          defaultClientId={meeting.client?.id ?? null}
          defaultProjectId={meeting.project?.id ?? null}
        />
      )}
    </div>
  );
}

function MetaItem({ icon: Icon, children }: { icon: typeof CalendarDays; children: ReactNode }) {
  return (
    <li className="inline-flex min-w-0 items-center gap-1.5">
      <Icon size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
      <span className="min-w-0 break-words">{children}</span>
    </li>
  );
}
